import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { farmbaseApi } from '../../services/api';
import { useTargetStore } from '../../store/targetStore';
import { useUnitStore } from '../../store/unitStore';
import { useAuthStore } from '../../store/authStore';
import { normalizeGeometry } from '../../lib/geo';
import type { Farmbase, Target } from '../../types/domain';
import { targetRenderClass } from './opsChips';

const FARMBASE_REFETCH_MS = 30_000;
const TARGET_REFETCH_MS = 15_000;

// Operational picture data for the Live Map: unit-scoped farmbases + targets.
// Lives at the page level so the SAME lists drive rendering (FarmbaseLayer,
// TargetsLayer, IntentArrows) and the auto-fit bounds (F-6).
export function useOperationalMap({ showInactiveTargets }: { showInactiveTargets: boolean }) {
  const { getEffectiveUnitIds, isHQ } = useUnitStore();
  const { user } = useAuthStore();
  const effectiveUnitIds = getEffectiveUnitIds(user?.unitId || null);
  const isUserHQ = isHQ(user?.unitId || null);

  // Farmbases: REST poll (no socket channel carries farmbase geometry edits).
  const { data: farmbaseData } = useQuery({
    queryKey: ['farmbases-map'],
    queryFn: async () => (await farmbaseApi.getAll()).data.farmbases as Farmbase[],
    refetchInterval: FARMBASE_REFETCH_MS,
    refetchOnWindowFocus: true,
  });

  const farmbases = useMemo(() => {
    return (farmbaseData ?? []).filter((fb) => {
      if (effectiveUnitIds) return effectiveUnitIds.includes(fb.unitId);
      if (user?.unitId && !isUserHQ) return fb.unitId === user.unitId;
      return true;
    });
  }, [farmbaseData, effectiveUnitIds, user, isUserHQ]);

  // Farmbases that actually have drawn geometry (chips/bounds only make sense for these).
  const farmbasesWithGeometry = useMemo(
    () => farmbases.filter((fb) => normalizeGeometry(fb) != null),
    [farmbases]
  );

  // Targets: the socket-fed store is the source of truth (upserts on target:*
  // events); poll as a safety net so a dropped socket still refreshes.
  const { targets: allTargets, fetchTargets } = useTargetStore();
  useEffect(() => {
    fetchTargets();
    const t = setInterval(fetchTargets, TARGET_REFETCH_MS);
    return () => clearInterval(t);
  }, [fetchTargets]);

  const targets = useMemo(() => {
    return allTargets.filter((t: Target) => {
      // Unit scope; unit-less targets are global (HQ-visible — the backend
      // already withholds them from non-HQ tokens).
      if (effectiveUnitIds && t.unitId && !effectiveUnitIds.includes(t.unitId)) return false;
      // Only ENDED targets hide behind the toggle. SCHEDULED (INACTIVE with a
      // window that hasn't closed) stays visible by default — an upcoming
      // target is the single most important thing to see while planning. This
      // list also feeds the auto-fit bounds, so scheduled targets get fitted.
      if (targetRenderClass(t) === 'ended' && !showInactiveTargets) return false;
      return normalizeGeometry(t) != null;
    });
  }, [allTargets, effectiveUnitIds, showInactiveTargets]);

  return { farmbases: farmbasesWithGeometry, targets };
}
